// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import type { CSSProperties, ReactNode } from "react"
import { useEvolutionCycle } from "@/queries/queries.evolutionCycles"
import { useForge } from "@/queries/queries.forges"
import { useCycleLatestSnapshot } from "@/queries/queries.eventJournal"
import { selectBudgetInfo } from "@/lib/cycle-config"
import { compareToForgeRecord } from "@/lib/forge-record"
import { computeCompletionEtaMin, computeTimeMetrics } from "@/lib/time-metrics"
import { isFiniteNumber } from "@/lib/number-guards"
import { formatCurrency } from "@/utils"
import { formatCycleDuration } from "@/views/evolutionCycles/cycleDuration"
import { parseCycleSnapshotPayload } from "../_snapshotPayload"
import { useParsedLatestProgress } from "../_useParsedLatestProgress"
import { selectAccumulatingTier } from "../_accumulatingTier"
import {
  selectExecutionTier,
  deriveExecutionRates,
} from "../_executionTier"
import { selectCurrentBestScore } from "../_bestScore"

export interface CycleHeadStripProps {
  cycleId: string
}

const DASH = "—"

const RING_RADIUS = 38
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

const formatMoney1 = (value: number): string => `$${value.toFixed(1)}`
const formatMoney = (value: number | null): string =>
  value == null ? DASH : formatCurrency(value)
const formatScore = (value: number | null): string =>
  value == null ? DASH : value.toFixed(2)
const formatAbsDelta = (value: number): string => Math.abs(value).toFixed(2)

const formatPctInt = (value: number | null): string =>
  value == null ? DASH : `${Math.round(value)}%`
const formatPct1 = (value: number | null): string =>
  value == null ? DASH : `${value.toFixed(1)}%`

// Metrics group thousands with a space; a comma separator reads as a decimal
// point to half the audience.
const groupThousands = (value: number, fractionDigits = 0): string => {
  const [int, frac] = value.toFixed(fractionDigits).split(".")
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
  return frac ? `${grouped}.${frac}` : grouped
}
const groupThousandsOrDash = (value: number | null): string =>
  value == null ? DASH : groupThousands(value)

const formatRate = (value: number | null, unit: string): string =>
  value == null ? DASH : `${groupThousands(Math.round(value))}${unit}`

const compactTokens = (
  n: number | null,
): { num: string; unit: string } | null => {
  if (n == null) return null
  if (n >= 1e6) return { num: (n / 1e6).toFixed(1), unit: "M" }
  if (n >= 1e3) return { num: String(Math.round(n / 1e3)), unit: "k" }
  return { num: String(n), unit: "" }
}
const compactTokensStr = (n: number | null): string => {
  const c = compactTokens(n)
  return c == null ? DASH : `${c.num}${c.unit}`
}
const compactRate = (
  n: number | null,
): { num: string; unit: string } | null => {
  if (n == null) return null
  if (n >= 1e3) return { num: groupThousands(Math.round(n / 1e3)), unit: "k tok/m" }
  return { num: String(Math.round(n)), unit: " tok/m" }
}

const formatEta = (etaMin: number | null): string => {
  if (etaMin == null) return DASH
  if (etaMin <= 0) return "exhausted"
  const totalMin = Math.round(etaMin)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return h > 0 ? `~${h}h ${m}m` : `~${m}m`
}

const STATUS_PILL: Record<CycleStatus, string> = {
  running: "border-primary/38 bg-primary/12 text-primary",
  completed: "border-done/38 bg-done/12 text-done-fg",
  failed: "border-failed/38 bg-failed/12 text-failed-fg",
  expired: "border-expired/38 bg-expired/12 text-expired-fg",
}

const legPct = (
  evalValue: number | null,
  metaValue: number | null,
): { evalPct: number; metaPct: number } => {
  const sum = (evalValue ?? 0) + (metaValue ?? 0)
  if (sum <= 0) return { evalPct: 0, metaPct: 0 }
  return {
    evalPct: ((evalValue ?? 0) / sum) * 100,
    metaPct: ((metaValue ?? 0) / sum) * 100,
  }
}

export default function CycleHeadStrip({ cycleId }: CycleHeadStripProps) {
  const cycleQuery = useEvolutionCycle(cycleId)
  const forgeQuery = useForge(cycleQuery.data?.forge_id)
  const snapshotQuery = useCycleLatestSnapshot(cycleId)
  const { progress } = useParsedLatestProgress(cycleId)

  const isLoading = cycleQuery.isLoading || snapshotQuery.isLoading

  if (isLoading || !cycleQuery.data) {
    return (
      <section
        role="region"
        aria-label="Cycle head (loading)"
        className="rounded-[16px] border border-primary/28 px-10 py-[18px]"
      >
        <p className="text-sm text-text-tertiary">Loading head…</p>
      </section>
    )
  }

  const cycle = cycleQuery.data
  const budgetInfo = selectBudgetInfo(cycle)

  let snapshot: CycleSnapshotDto | null = null
  if (snapshotQuery.isSuccess && snapshotQuery.data != null) {
    snapshot = parseCycleSnapshotPayload<CycleSnapshotDto>(
      snapshotQuery.data.payload_json,
    )
  }

  const accumulating = selectAccumulatingTier(progress, snapshot)
  const execution = selectExecutionTier(progress, snapshot)
  const rates = deriveExecutionRates(execution, accumulating.totalCalls)

  // --- AGENTS cluster ---
  const spent = accumulating.spent
  const hasBudget = isFiniteNumber(budgetInfo.budget) && budgetInfo.budget > 0
  const pctUsed =
    spent != null && hasBudget ? spent / (budgetInfo.budget as number) : null
  const ringFraction =
    pctUsed == null ? null : Math.max(0, Math.min(1, pctUsed))
  const ringOffset =
    ringFraction == null
      ? RING_CIRCUMFERENCE
      : RING_CIRCUMFERENCE * (1 - ringFraction)
  const budgetMicro =
    budgetInfo.budget != null
      ? `of ${formatMoney1(budgetInfo.budget)}`
      : "No budget set"

  const currentBestScore = selectCurrentBestScore(snapshot?.best_agent_scores)
  const recordComparison = compareToForgeRecord(
    currentBestScore,
    forgeQuery.data?.best_score,
  )

  // The Calls hero and every call-derived pace read the authoritative live
  // total (folded every event, fresh from the first tick). The per-phase
  // eval/meta ledgers only settle at round-end, so summing them would read 0
  // through the first round and freeze at 0 on an early-failing cycle (CORR-05).
  const callsTotal = accumulating.totalCalls
  // The per-phase split renders only where its round-end ledger data is
  // meaningful; a present-and-zero ledger (pre-first-round) renders "—", not a
  // fabricated 0 (INV-3).
  const callsSplitMeaningful =
    (execution.evalCalls ?? 0) + (execution.metaCalls ?? 0) > 0

  const timeMetrics = computeTimeMetrics(
    {
      totalCost: accumulating.spent,
      totalCalls: callsTotal,
      totalTokens: accumulating.totalTokens,
    },
    budgetInfo.budget,
    cycle.started_at,
    Date.now(),
    cycle.ended_at,
  )
  // The AGENTS line answers "when does this cycle finish?", not "when does the
  // budget run out?" — the budget-exhaustion horizon of ADR-0067 has no render
  // site on this strip.
  const completionEtaMin = computeCompletionEtaMin(
    {
      // The snapshot lands once an iteration closes, so `iter_index` names the
      // last closed one; no snapshot means the first iteration is still open.
      completedIterations: snapshot == null ? 0 : snapshot.iter_index + 1,
      agentsEvaluated: accumulating.agentsEvaluated,
      expectedPopulation: accumulating.expectedPopulation,
      maxIterations: budgetInfo.maxIterations,
    },
    cycle.started_at,
    Date.now(),
    cycle.ended_at,
  )
  // A terminal cycle has nothing left to project: the elapsed duration is
  // already frozen at `ended_at`, so the segment goes away entirely rather than
  // outliving the run it was estimating. It is dropped rather than dashed
  // because "—" is this strip's absent-value marker (INV-3/INV-5): a running
  // cycle whose inputs are degraded renders `eta —`, and reusing that glyph for
  // a finished one would make the two states indistinguishable. The status pill
  // above already says the cycle is done.
  const isTerminal = cycle.status !== "running" || cycle.ended_at != null
  // Running, inputs sound, nothing measured yet — no iteration has closed and
  // no agent has been evaluated, so `computeCompletionEtaMin` has nothing to
  // divide by. That is a known state, not a missing value, so it reads as a
  // word the way `exhausted` does; "—" is left to mean what it means
  // everywhere else on this strip, an input that never arrived.
  // Before the first iteration closes there is nothing to divide by, and the
  // inputs arrive in several shapes on the way there — no progress row at all,
  // a row without `agents_evaluated_this_iter`, a row reporting zero. All of
  // them mean the same thing to a reader, so they are not enumerated: while no
  // snapshot has landed, an uncomputable ETA is one that has not been measured
  // yet. Once a snapshot exists, an uncomputable ETA is genuinely degraded and
  // keeps its dash — as does a cycle with no iteration cap, which can never
  // earn a completion estimate at all.
  const notYetMeasured =
    snapshot == null &&
    completionEtaMin == null &&
    isFiniteNumber(budgetInfo.maxIterations) &&
    budgetInfo.maxIterations > 0
  const eta = isTerminal
    ? null
    : notYetMeasured
      ? "pending"
      : formatEta(completionEtaMin)

  // --- INFERENCE: Spend / Tokens / Calls / Retries ---
  const spendSplit = legPct(accumulating.evalSpend, accumulating.metaSpend)
  const tokenSplit = legPct(accumulating.promptTokens, accumulating.completionTokens)
  const callSplit = legPct(execution.evalCalls, execution.metaCalls)
  const retrySplit = legPct(execution.nCallsRetriedEval, execution.nCallsRetriedMeta)

  const totalTok = compactTokens(accumulating.totalTokens)
  const tput = compactRate(timeMetrics.tokensPerMin)

  return (
    <section
      role="region"
      aria-label="Cycle head"
      className="flex flex-col gap-[52px] rounded-[16px] border border-primary/28 px-10 py-[18px] lg:flex-row lg:items-center"
      style={{
        background:
          "linear-gradient(180deg,rgba(9,32,38,.85),rgba(6,24,28,.7))",
        boxShadow:
          "0 0 26px rgba(69,240,200,0.10), inset 0 1px 0 rgba(255,255,255,0.03)",
      }}
    >
      {/* AGENTS cluster — agent granularity (INV-9). */}
      <div
        role="group"
        aria-label="Agents"
        className="flex shrink-0 items-center gap-[22px]"
      >
        <div className="relative size-[88px] shrink-0">
          <svg
            width="88"
            height="88"
            viewBox="0 0 88 88"
            style={{ transform: "rotate(-90deg)" }}
          >
            <defs>
              <linearGradient id="strip-ring" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="var(--mint-deep)" />
                <stop offset="1" stopColor="var(--mint)" />
              </linearGradient>
            </defs>
            <circle
              cx="44"
              cy="44"
              r={RING_RADIUS}
              fill="none"
              stroke="#0E2E33"
              strokeWidth="7"
            />
            {ringFraction != null && (
              <circle
                cx="44"
                cy="44"
                r={RING_RADIUS}
                fill="none"
                stroke="url(#strip-ring)"
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={ringOffset}
                style={{ filter: "drop-shadow(0 0 6px rgba(69,240,200,0.5))" }}
              />
            )}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[17px] font-semibold text-foreground">
              {spent == null ? DASH : formatMoney1(spent)}
            </span>
            <span className="mt-0.5 font-mono text-[9px] font-medium uppercase tracking-[0.08em] text-text-muted">
              {budgetMicro}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span
            data-status={cycle.status}
            className={`inline-flex items-center gap-1.5 self-start rounded-full border py-[3px] pl-2 pr-[9px] font-mono text-[10px] font-medium ${
              STATUS_PILL[cycle.status]
            }`}
          >
            <span
              aria-hidden
              className="size-1.5 rounded-full bg-current"
              style={{ boxShadow: "0 0 7px currentColor" }}
            />
            {cycle.status}
          </span>

          <div className="flex items-baseline gap-[9px]">
            <span
              className="text-[38px] font-semibold leading-[0.9] tracking-[-0.02em] text-done-fg"
              style={{ textShadow: "0 0 16px rgba(104,204,88,0.4)" }}
            >
              {formatScore(recordComparison.current)}
            </span>
            {recordComparison.delta != null && (
              <span
                className={`font-mono text-[11.5px] font-medium ${
                  recordComparison.delta >= 0 ? "text-done-fg" : "text-expired-fg"
                }`}
              >
                {`${recordComparison.delta >= 0 ? "▲" : "▼"} ${
                  recordComparison.delta >= 0 ? "+" : "−"
                }${formatAbsDelta(recordComparison.delta)}`}
              </span>
            )}
          </div>

          <span className="font-mono text-[10.5px] font-medium text-text-tertiary">
            {formatCycleDuration(cycle.started_at, cycle.ended_at)}
            {eta != null && (
              <>
                {" · eta "}
                <span className={eta === DASH ? "text-text-muted" : undefined}>
                  {eta}
                </span>
              </>
            )}
          </span>

          <span className="font-mono text-[10.5px] font-medium text-text-tertiary">
            fail · eval{" "}
            <b className="font-medium text-primary">
              {formatPctInt(rates.evalFailPct)}
            </b>{" "}
            · meta{" "}
            <b className="font-medium text-[#8AB4F8]">
              {formatPctInt(rates.metaFailPct)}
            </b>
          </span>
        </div>
      </div>

      <div className="hidden w-px self-stretch bg-[#0E2E33] lg:block" />

      {/* INFERENCE metrics — call granularity (INV-9). */}
      <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-12 gap-y-6 lg:grid-cols-4">
        <InferenceMetric
          label="Spend"
          hero={formatMoney(accumulating.spent)}
          split={spendSplit}
          leftLabel="eval"
          leftValue={formatMoney(accumulating.evalSpend)}
          rightLabel="meta"
          rightValue={formatMoney(accumulating.metaSpend)}
          rate={
            timeMetrics.burnPerMin == null
              ? DASH
              : `${formatMoney(timeMetrics.burnPerMin)} /min`
          }
        />
        <InferenceMetric
          label="Tokens"
          hero={totalTok?.num ?? DASH}
          heroUnit={totalTok?.unit}
          split={tokenSplit}
          leftLabel="in"
          leftValue={compactTokensStr(accumulating.promptTokens)}
          rightLabel="out"
          rightValue={compactTokensStr(accumulating.completionTokens)}
          rate={tput == null ? DASH : `${tput.num}${tput.unit}`}
        />
        <InferenceMetric
          label="LLM Calls"
          hero={groupThousandsOrDash(callsTotal)}
          split={callSplit}
          leftLabel="eval"
          leftValue={
            callsSplitMeaningful
              ? groupThousandsOrDash(execution.evalCalls)
              : DASH
          }
          rightLabel="meta"
          rightValue={
            callsSplitMeaningful
              ? groupThousandsOrDash(execution.metaCalls)
              : DASH
          }
          rate={formatRate(timeMetrics.callsPerMin, " /min")}
        />
        <InferenceMetric
          label="LLM Retries"
          hero={formatPct1(rates.overallRetryPct)}
          split={retrySplit}
          leftLabel="eval"
          leftValue={formatPct1(rates.evalRetryPct)}
          rightLabel="meta"
          rightValue={formatPct1(rates.metaRetryPct)}
          rate={
            <>
              exhausted{" "}
              <b className="font-medium text-failed-fg">
                {execution.nExhausted == null
                  ? DASH
                  : groupThousands(execution.nExhausted)}
              </b>
            </>
          }
        />
      </div>
    </section>
  )
}

const MINT = "bg-primary"
const BLUE = "bg-[#5B8DEF]"

const InferenceMetric = ({
  label,
  hero,
  heroUnit,
  split,
  leftLabel,
  leftValue,
  rightLabel,
  rightValue,
  rate,
}: {
  label: string
  hero: ReactNode
  heroUnit?: string
  split: { evalPct: number; metaPct: number }
  leftLabel: string
  leftValue: ReactNode
  rightLabel: string
  rightValue: ReactNode
  rate: ReactNode
}) => (
  <div
    role="group"
    aria-label={label}
    className="flex flex-col gap-[9px]"
  >
    <div className="flex items-baseline justify-between gap-2">
      <span className="font-mono text-[11px] font-medium uppercase tracking-[0.06em] text-text-tertiary">
        {label}
      </span>
      <span className="text-[26px] font-semibold leading-none tracking-[-0.02em] text-foreground">
        {hero}
        {heroUnit ? (
          <small className="text-[0.42em] font-medium text-text-tertiary">
            {heroUnit}
          </small>
        ) : null}
      </span>
    </div>
    <div className="flex h-[5px] overflow-hidden rounded-[5px] bg-[#0E2E33]">
      <span
        data-leg="eval"
        className={`block h-full ${MINT}`}
        style={{ width: `${split.evalPct}%` }}
      />
      <span
        data-leg="meta"
        className={`block h-full ${BLUE}`}
        style={{ width: `${split.metaPct}%` }}
      />
    </div>
    <span className="font-mono text-[10.5px] font-medium text-text-tertiary">
      {leftLabel}{" "}
      <b data-leg="eval" className="font-medium text-primary">
        {leftValue}
      </b>{" "}
      · {rightLabel}{" "}
      <b data-leg="meta" className="font-medium text-[#8AB4F8]">
        {rightValue}
      </b>
    </span>
    <span
      className="font-mono text-[10.5px] font-medium text-text-muted"
      style={{ minHeight: "1em" } as CSSProperties}
    >
      {rate}
    </span>
  </div>
)
